import { Target } from 'lucide-react';
import { motion } from 'motion/react';

import { SearchResult } from '../../hooks/useCommandPalette';
import { CommandEmpty, CommandGroup, CommandItem } from '../ui/command';

interface SearchResultsProps {
  loading?: boolean;
  onHover?: (result: SearchResult) => void;
  onSelect: (result: SearchResult) => void;
  opportunities: SearchResult[];
}

const formatMetadata = (result: SearchResult): string => {
  const parts: string[] = [];
  if (result.metadata?.stage) parts.push(result.metadata.stage);
  if (result.metadata?.status) parts.push(result.metadata.status);
  return parts.join(' · ');
};

const LoadingSkeleton = () => (
  <div className="space-y-2 px-3 py-2">
    {[1, 2, 3].map(i => (
      <div
        className="flex items-center gap-3"
        key={i}
      >
        <div className="bg-muted h-4 w-4 animate-pulse rounded" />
        <div className="flex-1 space-y-1">
          <div className="bg-muted h-4 w-3/4 animate-pulse rounded" />
          <div className="bg-muted h-3 w-1/2 animate-pulse rounded" />
        </div>
      </div>
    ))}
  </div>
);

export function SearchResults({
  loading,
  onHover,
  onSelect,
  opportunities
}: SearchResultsProps) {
  if (loading) {
    return (
      <CommandGroup heading="Investment Opportunities">
        <LoadingSkeleton />
      </CommandGroup>
    );
  }

  if (opportunities.length === 0) {
    return (
      <CommandEmpty>
        <div className="py-6 text-center text-sm">
          <p className="text-muted-foreground">No results found</p>
          <p className="text-muted-foreground mt-2 text-xs">
            Try searching with different keywords
          </p>
        </div>
      </CommandEmpty>
    );
  }

  return (
    <>
      {opportunities.length > 0 && (
        <CommandGroup
          heading={`Investment Opportunities (${opportunities.length})`}
        >
          {opportunities.map((opportunity, index) => {
            const metadata = formatMetadata(opportunity);

            return (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: -10 }}
                key={opportunity.id}
                transition={{ delay: index * 0.03, duration: 0.2 }}
              >
                <CommandItem
                  className="flex items-center gap-3 px-3 py-2"
                  onMouseEnter={() => onHover?.(opportunity)}
                  onSelect={() => {
                    onSelect(opportunity);
                  }}
                  value={`opportunity-${opportunity.id}-${opportunity.name}`}
                >
                  <Target className="text-muted-foreground h-4 w-4" />
                  <div className="flex flex-1 flex-col overflow-hidden">
                    <span className="truncate font-medium">
                      {opportunity.name}
                    </span>
                    {metadata && (
                      <span className="text-muted-foreground truncate text-xs">
                        {metadata}
                      </span>
                    )}
                  </div>
                </CommandItem>
              </motion.div>
            );
          })}
        </CommandGroup>
      )}
    </>
  );
}
